import { get, has, isEmpty, omit, pick, snakeCase } from "lodash";
import { withQuery } from "ufo";
import type { LocationQuery } from "vue-router";
import { DashboardAppNav } from "../models";

const resourceNames = [
  "app",
  "business_profile",
  "category",
  "checkout_link",
  "customer",
  "discount_code",
  "enquiry",
  "event",
  "event_booking",
  "event_profile",
  "event_ticket",
  "event_venue",
  "faq",
  "file",
  "giftcard",
  "location",
  "order",
  "plan",
  "plan_contract",
  "platform_charge",
  "price_rule",
  "product",
  "product_variant",
  "product_type",
  "publisher",
  "recipe",
  "recipe_variant",
  "sales_channel",
  "seller",
  "service",
  "service_profile",
  "shipping_profile",
  "store",
  "store_order",
  "supplier",
  "team_member",
  "user",
] as const;

type ResolvedResource = {
  name: string;
  url: string;
  type: string;
};

type ResourceName = (typeof resourceNames)[number];

const aliasNames: Record<string, ResourceName> = {
  order: "store_order",
  voucher: "discount_code",
};

type ResourceType = ResourceName | keyof typeof aliasNames;

type Resource = {
  gid?: string;
  hashId?: string;
  objectName?: string;
  id?: string;
  handle?: string;
  name?: string;
  created_at?: string;
  updated_at?: string;
};

type RouteAction = {
  title: string;
  list: string;
  view: {
    name: string;
    params: string[];
    altParams?: Record<string, string>;
  };
};

const resourceIcons = {
  product: "grid_on",
  store_order: "storefront",
  // tag: 'tag',
  giftcard: "card_giftcard",
  category: "category",
  shipping_profile: "directions_boat",
  // return_policy: 'policy',
  order: "attach_money",
  // return: 'assignment_return',
  // abandoned_cart: 'remove_shopping_cart',
  team_member: "groups",
  // reseller: 'assignment_ind',
  user: "account_box",
  // review: 'feedback',
  // role: 'security',
  // company: 'business_center',
  // code: 'camera',
  supplier: "business",
  // resource: 'mdi-newspaper',
};

export function getResourceIcon(resource: ResourceType, _default = "app") {
  const name = actualResourceName(resource);
  if (!name) {
    return _default;
  }
  return get(resourceIcons, name, _default);
}

export function actualResourceName(
  resource: ResourceType
): ResourceName | undefined {
  let name: any = snakeCase(resource);
  if (has(aliasNames, name)) {
    name = get(aliasNames, name);
  }
  if (resourceNames.includes(name)) {
    return name;
  }
  return undefined;
}

export const resolveResource = (
  resource: Resource,
  options?: {
    resourceTypeKey?: string;
    hashIdKey?: string;
    resourceIdKey?: string;
    resourceNameKey?: string;
  }
) => {
  if (resource) {
    const config = useAppConfig();
    const objectNameKey = options?.resourceTypeKey || "objectName";
    const hashIdKey = options?.hashIdKey || "hashId";
    const resourceIdKey = options?.resourceIdKey;
    const resourceNameKey = options?.resourceNameKey || "name";

    let objectName: ResourceName | undefined = undefined;

    // refer hash ID
    const id = execFn(() => {
      if (resourceIdKey) {
        return String(
          get(resource, resourceIdKey) ||
            get(resource, hashIdKey) ||
            get(resource, "id") ||
            ""
        );
      }
      return String(get(resource, hashIdKey) || get(resource, "id") || "");
    });

    const _id = id.split("_");
    if (_id.length == 2) {
      const prefix = _id[0];
      // @ts-expect-error hashIds has object names
      objectName = config.app.resource.hashIds[prefix];
    }

    if (!objectName) {
      objectName = get(resource, objectNameKey) as ResourceName;
    }

    if (!objectName) {
      return;
    }

    const resourceRoute: RouteAction | undefined = get(
      config.app.resource.routes,
      objectName
    );

    if (!resourceRoute) {
      return;
    }

    const name = get(resource, resourceNameKey) || resourceRoute.title;
    const params: any = pick(resource, resourceRoute.view.params);

    if (isEmpty(params)) {
      if (resourceRoute.view.altParams) {
        Object.entries(resourceRoute.view.altParams).forEach(([a, b]) => {
          params[a] = get(resource, b);
        });
        return {
          name,
          objectName,
          route: {
            name: resourceRoute.view.name,
            params,
          },
        };
      } else {
        return {
          name,
          objectName,
          route: {
            name: resourceRoute.list,
          },
        };
      }
    } else {
      return {
        name,
        objectName,
        route: {
          name: resourceRoute.view.name,
          params,
        },
      };
    }
  }
};

export const resolveResourceUrl = (
  resource: Resource
): ResolvedResource | undefined => {
  if (resource) {
    const res = resolveResource(resource);

    if (!res) {
      return;
    }

    const url = execFn(() => {
      const router = useRouter();
      const route = router.resolve(res.route);
      return route?.href;
    });

    if (url && res.objectName) {
      return {
        name: res.name || res.objectName,
        type: res.objectName,
        url,
      };
    }
  }
};

export const handleUrlAction = (query: LocationQuery) => {
  const action = get(query, "action") as string;
  const omitQueries = ["action"];

  const q = Object.assign({}, query);
  let resource: Resource | undefined;
  for (const k in q) {
    if (k.startsWith("res_")) {
      resource = (resource || {}) as Resource;
      omitQueries.push(k);
      const rK = k.replace("res_", "") as keyof Resource;
      resource[rK] = q[k] as any;
    }
  }

  const navigate = (dest: string) => {
    const url = withQuery(dest, omit(query, omitQueries));
    window.location.href = url;
  };

  switch (action) {
    case "view":
      if (resource) {
        const resolvedResource = resolveResourceUrl(resource);
        if (resolvedResource) {
          navigate(resolvedResource.url);
          break;
        }
      }
    default: {
      if (action.startsWith("nav:")) {
        const nav = action.replace("nav:", "") as DashboardAppNav;
        switch (nav) {
          case DashboardAppNav.TEAM_MEMBERS:
            navigateTo({ name: "settings-team" });
            break;
        }
        //
      } else {
        // use action to resolve routes
        const config = useAppConfig();
        const resourceNames = Object.values(config.app.resource.hashIds);
        if (resourceNames.includes(action)) {
          const resolvedResource = resolveResourceUrl({
            ...resource,
            objectName: action,
          });

          if (resolvedResource) {
            navigate(resolvedResource.url);
          }
        }
      }
    }
  }
};
